import { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Eye } from 'react-feather';
import EditIcon from '../../../assets/svg/edit.svg';
import CameraIcon from '../../../assets/svg/Camera.svg';
import TrashIcon from '../../../assets/svg/Trash.svg';
import { IMAGE_UPLOAD_LABELS } from './constants';
import { isValidFilename } from '../../utils/fileValidation';
import { validateFileSignature } from '../../utils/fileSignatureValidation';
import { SHARED_FILE_ERRORS } from '../../constants/fileErrors';

interface ImageUploadProps {
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDelete?: (() => void) | undefined;
  onError?: ((message: string) => void) | undefined;
  onPreviewClick?: (() => void) | undefined;
  disabled?: boolean;
  initialLetter?: string;
  profileFile?: File | undefined;
  existingImageUrl?: string | undefined;
  addButtonLabel?: string;
  maxSizeMB?: number;
  allowedTypes?: string[];
}

export const ImageUpload = ({
  onFileChange,
  onDelete,
  onError,
  onPreviewClick,
  disabled = false,
  initialLetter = 'A',
  profileFile,
  existingImageUrl,
  addButtonLabel = IMAGE_UPLOAD_LABELS.CHANGE_PROFILE,
  maxSizeMB = 5,
  allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'],
}: ImageUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const circleRef = useRef<HTMLDivElement>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const updateTooltipPosition = () => {
    if (!circleRef.current) {
      return;
    }
    const rect = circleRef.current.getBoundingClientRect();
    setTooltipPos({
      top: rect.bottom + 8,
      left: rect.left + rect.width / 2,
    });
  };

  const handleTooltipEnter = () => {
    updateTooltipPosition();
    setShowTooltip(true);
  };

  const handleTooltipLeave = () => {
    setShowTooltip(false);
  };

  useEffect(() => {
    if (profileFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(profileFile);
    } else if (existingImageUrl) {
      setImagePreview(existingImageUrl);
    } else {
      setImagePreview(null);
    }
  }, [profileFile, existingImageUrl]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const handleCircleClick = () => {
    if (!disabled) {
      setShowMenu((prev) => !prev);
    }
  };

  const handlePreviewIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled && onPreviewClick) {
      onPreviewClick();
    }
  };

  const handleEditIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      setShowMenu((prev) => !prev);
    }
  };

  const handleChange = () => {
    fileInputRef.current?.click();
    setShowMenu(false);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate filename
      if (!isValidFilename(file.name)) {
        if (onError) {
          onError(SHARED_FILE_ERRORS.INVALID_FILENAME);
        }
        // Reset file input to allow re-selection
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        if (onError) {
          onError(SHARED_FILE_ERRORS.FILE_FORMAT);
        }
        // Reset file input to allow re-selection
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Validate file size
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        if (onError) {
          onError(SHARED_FILE_ERRORS.FILE_SIZE);
        }
        // Reset file input to allow re-selection
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Validate file signature
      const isValidSignature = await validateFileSignature(file, allowedTypes);
      if (!isValidSignature) {
        if (onError) {
          onError(SHARED_FILE_ERRORS.FILE_CORRUPTED);
        }
        // Reset file input to allow re-selection
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // File is valid, proceed
      onFileChange(e);
      // Reset file input after successful upload
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
    setShowMenu(false);
  };

  return (
    <div
      className={`relative ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      onClick={handleCircleClick}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleCircleClick();
        }
      }}
      data-testid="profile-image-upload"
    >
      <div
        ref={circleRef}
        onMouseEnter={
          imagePreview && onPreviewClick ? handleTooltipEnter : undefined
        }
        onMouseLeave={
          imagePreview && onPreviewClick ? handleTooltipLeave : undefined
        }
        className="group relative w-[92px] h-[92px] sm:w-[80px] sm:h-[80px] lg:w-[100px] lg:h-[100px] rounded-full bg-[#E8EEF0] flex items-center justify-center"
      >
        <div className="w-full h-full rounded-full overflow-hidden flex items-center justify-center">
          {imagePreview ? (
            <img
              src={imagePreview}
              alt="Profile"
              className="w-full h-full object-cover"
            />
          ) : (
            <span
              className="text-[25.3px] sm:text-[22px] lg:text-[28px] leading-[32.2px] sm:leading-[28px] lg:leading-[36px] font-normal text-center text-[#223746]"
              style={{ fontFamily: 'Roboto' }}
            >
              {initialLetter}
            </span>
          )}
        </div>
        {imagePreview && onPreviewClick && !disabled && (
          <>
            <button
              type="button"
              onClick={handlePreviewIconClick}
              className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100 focus:outline-none cursor-pointer rounded-full"
              aria-label="Wyświetl zdjęcie profilowe"
              data-testid="profile-image-preview"
            >
              <Eye size={24} color="#FFFFFF" />
            </button>
          </>
        )}
      </div>

      {showTooltip &&
        imagePreview &&
        onPreviewClick &&
        createPortal(
          <div
            className="fixed flex flex-col items-center pointer-events-none"
            style={{
              top: `${tooltipPos.top}px`,
              left: `${tooltipPos.left}px`,
              transform: 'translateX(-50%)',
              zIndex: 9999,
            }}
            data-testid="profile-image-preview-tooltip"
          >
            <svg
              width="16"
              height="8"
              viewBox="0 0 16 8"
              fill="none"
              style={{ marginBottom: '-1px' }}
            >
              <path
                d="M0 8L16 8L9.41421 1.41421C8.63317 0.633166 7.36684 0.633166 6.58579 1.41421L0 8Z"
                fill="#181A1B"
              />
            </svg>
            <div
              className="px-2 py-1 rounded bg-[#181A1B] text-white text-[12px] leading-[16px] whitespace-nowrap"
              style={{
                fontFamily: 'Roboto',
                letterSpacing: '0.4px',
              }}
            >
              Wyświetl zdjęcie profilowe
            </div>
          </div>,
          document.body
        )}

      <div
        className="absolute bottom-0 right-0 w-[32px] h-[32px] sm:w-[28px] sm:h-[28px] lg:w-[36px] lg:h-[36px]"
        onClick={handleEditIconClick}
      >
        <EditIcon />
      </div>

      {showMenu && (
        <div
          ref={menuRef}
          className="absolute bottom-[-56px] left-[100px] sm:left-[85px] lg:left-[110px] w-[208px] bg-white py-1 flex flex-col rounded z-50"
          style={{
            boxShadow: '0px 2px 6px 2px #00000014, 0px 1px 2px 0px #00000026',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={handleChange}
            disabled={disabled}
            className={`px-3 py-2 text-left hover:bg-gray-100 flex items-center gap-2 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            style={{
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.1px',
              color: '#181A1B',
            }}
            data-testid="profile-image-change"
          >
            <div
              style={{
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CameraIcon />
            </div>
            <span className="text-[14px]">{addButtonLabel}</span>
          </button>
          <button
            type="button"
            onClick={imagePreview ? handleDelete : () => setShowMenu(false)}
            disabled={disabled || !imagePreview}
            className={`px-3 py-2 text-left hover:bg-gray-100 flex items-center gap-2 ${disabled || !imagePreview ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            style={{
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.1px',
              color: '#E11B1B',
            }}
            data-testid="profile-image-delete"
          >
            <div
              style={{
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <TrashIcon />
              </div>
            </div>
            <span className="text-[14px]">{IMAGE_UPLOAD_LABELS.DELETE}</span>
          </button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={allowedTypes.join(',')}
        onChange={handleFileSelect}
        disabled={disabled}
        className="hidden"
        aria-label="Upload profile picture"
      />
    </div>
  );
};
